'use client';

import { useState } from 'react';

export default function EarlyAccessForm({ lang }: { lang: string }) {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    project_type: 'ecommerce',
    comment: '',
  });

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const isUk = lang === 'uk';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const res = await fetch('/api/marketing/early-access', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || (isUk ? 'Сталася помилка при надсиланні. Спробуйте ще раз.' : 'An error occurred. Please try again.'));
      }

      setStatus('success');
    } catch (err: any) {
      setStatus('error');
      setErrorMessage(err.message || (isUk ? 'Помилка з’єднання. Перевірте інтернет.' : 'Connection error. Check your network.'));
    }
  };

  if (status === 'success') {
    return (
      <div className="bg-white p-8 md:p-12 rounded-3xl shadow-xl border border-gray-100 text-center max-w-xl mx-auto animate-fade-in">
        <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
          ✓
        </div>
        <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-4">
          {isUk ? 'Вашу заявку прийнято!' : 'Welcome to the waitlist!'}
        </h2>
        <p className="text-gray-600 mb-6 leading-relaxed">
          {isUk
            ? 'Дякуємо за інтерес до Volosyni. Ми повідомимо вас на електронну пошту, щойно відкриється наступна хвиля доступу. Перші користувачі отримають 1 місяць тарифу Pro у подарунок!'
            : 'Thank you for your interest in Volosyni. We will email you an invite as soon as the next wave opens. Early members receive 1 free month of Pro!'}
        </p>
        <div className="p-4 bg-blue-50 border border-blue-100 rounded-2xl text-sm text-blue-900 font-medium">
          {isUk ? '🎁 Бонус зарезервовано за адресою: ' : '🎁 Bonus reserved for: '}
          <span className="font-bold underline">{formData.email}</span>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white p-8 md:p-10 rounded-3xl shadow-xl border border-gray-100 max-w-xl mx-auto text-left">
      {status === 'error' && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl flex items-center gap-2">
          <svg className="w-5 h-5 flex-shrink-0 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{errorMessage}</span>
        </div>
      )}

      <div className="mb-5">
        <label htmlFor="email" className="block text-sm font-semibold text-gray-800 mb-2">
          {isUk ? 'Ваша електронна пошта *' : 'Email address *'}
        </label>
        <input
          id="email"
          type="email"
          required
          placeholder="you@domain.com"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 text-base outline-none transition"
        />
        <p className="mt-1 text-xs text-gray-400">
          {isUk ? 'Ми ніколи не розсилаємо спам і дбаємо про захист ваших даних.' : 'We respect your inbox and never send spam.'}
        </p>
      </div>

      <div className="mb-5">
        <label htmlFor="name" className="block text-sm font-semibold text-gray-800 mb-2">
          {isUk ? 'Ваше ім’я або назва бренду' : 'Your name or brand'}
        </label>
        <input
          id="name"
          type="text"
          placeholder={isUk ? 'Олексій / Студія краси' : 'Alex / Creative Studio'}
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 text-base outline-none transition"
        />
      </div>

      <div className="mb-5">
        <label htmlFor="project_type" className="block text-sm font-semibold text-gray-800 mb-2">
          {isUk ? 'Тип проєкту' : 'Project type'}
        </label>
        <select
          id="project_type"
          value={formData.project_type}
          onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 text-base outline-none transition bg-white"
        >
          <option value="ecommerce">{isUk ? '🛍 Інтернет-магазин / Продажі' : '🛍 E-commerce / Online store'}</option>
          <option value="portfolio">{isUk ? '📸 Портфоліо / Фотограф / Творець' : '📸 Portfolio / Creator'}</option>
          <option value="services">{isUk ? '💼 Послуги / Салон / Агенція' : '💼 Services / Agency'}</option>
          <option value="charity">{isUk ? '🤝 Благодійний фонд / ГО' : '🤝 Charity / Non-profit'}</option>
          <option value="landing">{isUk ? '🚀 Лендінг / Промо-сайт' : '🚀 Landing page / Campaign'}</option>
          <option value="other">{isUk ? '✨ Інше' : '✨ Other'}</option>
        </select>
      </div>

      <div className="mb-6">
        <label htmlFor="comment" className="block text-sm font-semibold text-gray-800 mb-2">
          {isUk ? 'Які функції для вас найбільш важливі?' : 'What features are you most excited about?'}
        </label>
        <textarea
          id="comment"
          rows={3}
          placeholder={isUk ? 'Наприклад: інтеграція з Новою Поштою, Telegram сповіщення, CRM...' : 'E.g. visual builder, custom domains, CRM...'}
          value={formData.comment}
          onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 text-base outline-none transition resize-none"
        />
      </div>

      <button
        type="submit"
        disabled={status === 'loading'}
        className="w-full py-4 bg-gray-900 hover:bg-blue-600 text-white font-bold text-base rounded-xl transition shadow-lg shadow-gray-200 disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {status === 'loading' ? (
          <>
            <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span>{isUk ? 'Надсилаємо...' : 'Submitting...'}</span>
          </>
        ) : (
          <span>{isUk ? 'Подати заявку на ранній доступ' : 'Request Early Access'}</span>
        )}
      </button>

      <p className="mt-4 text-center text-xs text-gray-400">
        {isUk
          ? '🔒 Безпека даних гарантована. Ми не передаємо ваші контакти третім сторонам.'
          : '🔒 Data privacy guaranteed. Your details are safe with us.'}
      </p>
    </form>
  );
}
